import StateObserver, { user, now } from 'src/StateObserver';
import type {
  PromoGroupID,
  PromoGroupItem,
  PromoGroupItemID,
  PromoGroupItemsMap,
} from 'src/replicant/promo/group/promoGroup.types';
import {
  launchPromoSlotsAvailable,
  promoGroupConfig,
} from 'src/replicant/promo/group/promoGroup.ruleset';
import {
  getReadyGroupItems,
  isGroupItemReady,
} from 'src/replicant/promo/group/promoGroup.getters';
import features from 'src/replicant/ruleset/features';
import * as Social from 'src/replicant/social';
import { Feature } from 'src/replicant/analytics';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { isPlatformGuest } from 'src/lib/utils';
import { isFirstEntryOfDay } from 'src/lib/platformUtils';
import { isPlatformLiff } from 'src/game/crossplay/crossplay.utils';
import {
  magicLampsGetRewardSet,
  magicLampsIsAvailable,
} from 'src/replicant/match2/magiclamps/magiclamps.getters';
import {
  canParticipateOnCurrentRace,
  getActiveRace,
  getActiveRaceIndex,
  getUnclaimedRaces,
} from 'src/replicant/match2/races/races.getters';
import {
  isMatch2FrenzyEnabled,
  isMatch2FrenzyUnlocked,
  getMatch2FrenzyIntroLastSeen,
} from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import { getCurrentPetState } from 'src/replicant/fate/fate.getters';
import {
  getSuperLollipopLastSeen,
  isSuperLollipopAvailable,
} from 'src/replicant/superLollipop/superLollipop.getters';
import {
  isStreakBattleRoyaleEnabled,
  isStreakBattleRoyaleActive,
  isStreakBattleRoyaleRewarded,
} from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { getOngoingTreasureExpressEvent } from 'src/replicant/treasureExpress/treasureExpress.getters';
import { getUnclaimedMilestones } from 'src/replicant/treasureExpress/treasureExpress.getters';
import { puzzleCoinsIsPlayingTutorial } from 'src/replicant/puzzlecoins/puzzlecoins.getters';
import {
  jankenHasSeenTutorial,
  jankenIsAvailable,
  jankenIsEventActive,
} from 'src/replicant/janken/janken.getters';
import {
  buddiesHasSeenAnyEvent,
  buddiesIsAvailable,
  buddiesIsLocked,
} from 'src/replicant/buddies/buddies.getters';
import { cardsShouldAutoOpenFirstTime } from 'src/replicant/cards/cards.getters';
import {
  dreamDriftHasRewardsAvailable,
  dreamDriftShouldAutoOpenCurrentEvent,
} from 'src/replicant/dreamDrift/dreamDrift.getters';
import {
  getPromoShownCounter,
  isPromoReady,
} from 'src/replicant/promo/promo.getters';
import {
  canPlayLeague,
  hasJoinedLeague,
} from 'src/replicant/match2/leagues/leagues.getters';
import { isNativePromoAvailable } from '../native/native.util';
import { hasGiftsMessages, isGiftsFeatureUnlocked } from '../gifts/helpers';
import { magicLampsLaunch } from '../minigame/match2-timestep/magiclamps/magiclamps.sequence';
import { openRaceSequence } from '../minigame/match2-timestep/races/races.sequence';
import { openMatch2FrenzyIntroPopup } from '../minigame/match2-timestep/frenzy/match2-frenzy.sequence';
import { superLollipopLaunch } from '../superLollipop/superLollipop.sequence';
import { openStreakBattleRoyalePopup } from '../streakbattleroyale/streakbattleroyale.sequence';
import { homeShortcutLaunchSequence } from '../homeShortcut/homeShortcut.sequence';
import { treasureExpressLaunch } from '../treasureExpress/treasureExpress.sequence';
import {
  getJankenBlockedReason,
  isJankenInviteEntry,
  isJankenResultEntry,
  jankenSenderSequence,
} from '../janken/janken.sequence';
import {
  buddiesLaunchSequence,
  buddiesOpenMainPopup,
} from '../buddies/buddies.sequence';
import { buddiesGetUnseenPartyJoinerIds } from '../buddies/buddies.utils';
import { cardsAutoOpenFirstTime } from '../cards/cards.sequence';
import { openGiftsListPopup } from '../gifts/gifts.sequence';
import { startInviteFriendsSequence } from '../social/social.sequence';
import { shouldShowGachaPromo, showGachaPromo } from '../gacha/gacha.sequence';
import { joinLeague } from '../minigame/match2-timestep/leagues/league.sequences';
import { dreamDriftOpen } from '../dreamDrift/dreamDrift.sequence';

/** Frontend surface that can display one launch promo item. */
type PromoItemSurface = {
  /** Checks whether this surface is currently available. */
  isAvailable: () => boolean;

  /** Checks whether an available surface must display even with no slot. */
  shouldExempt?: () => boolean;

  /** Runs the surface and returns whether the item was resolved. */
  launch: () => Promise<boolean>;

  /** Runs alternate exempt content and reports whether the item was resolved. */
  launchExempt?: () => Promise<boolean>;
};

/** Complete set of launch surfaces organized by promo group. */
type PromoLaunchItems = {
  [GroupID in PromoGroupID]: Record<
    PromoGroupItemsMap[GroupID],
    PromoItemSurface
  >;
};

/**
 * Launch surfaces for daily features.
 *
 * Daily features can consume both shared launch slots. Their priority remains
 * defined by `promoGroupConfig` in the Replicant ruleset.
 */
const dailyFeatures: PromoLaunchItems['dailyFeatures'] = {
  /**
   * Gifts list (Action dialog, recurring daily).
   *
   * Available whenever gifts are unlocked. Closing the popup resolves the item
   * for the day; pending received gifts keep it exempt so it opens on every
   * launch without taking a slot.
   *
   * Popup: Gifts list. Selecting invite hands off to the invite flow and
   * resolves the daily friend invite item.
   */
  gifts: {
    isAvailable: () => isGiftsFeatureUnlocked(user()),
    // Pending received gifts open the popup on every launch and never take a slot.
    shouldExempt: () => hasGiftsMessages(user(), now()),
    launch: async () => {
      const result = await openGiftsListPopup('launch');
      // Gifts already handed off to the invite flow: resolve the daily invite item,
      // the same way feature sequences resolve their own promo items.
      if (result === 'invite') {
        await resolvePromoGroupFeature({
          groupId: 'dailyFeatures',
          itemId: 'friendInvite',
        });
      }
      // Show-resolved: closing counts for the day. Gift state is untouched.
      return true;
    },
  },

  /**
   * Friend Invite (Action dialog, recurring).
   *
   * Available while friend invites are unlocked and the player is not using a
   * guest platform. On LINE the item cooldown in `promoGroupConfig` sets the
   * frequency: every launch up to install day 7, then 6 hours since the last
   * automatic popup. Native apps also keep the first-entry-of-day gate.
   * Closing or cancelling the popup resolves the item and starts the cooldown.
   * Gifts resolves this item when the player invites from the gifts popup.
   *
   * Popup: Existing friend invite sequence with launch analytics attribution.
   */
  friendInvite: {
    isAvailable: () =>
      !isPlatformGuest(platform) &&
      Social.getters.isFriendInviteAvailable(user()) &&
      // LINE frequency comes from the item cooldown. Native keeps the daily gate.
      (isPlatformLiff() || isFirstEntryOfDay()) &&
      // processGroup snapshots readiness before its loop; Gifts may have resolved
      // this item earlier in the same pass, so re-check the live state here.
      isGroupItemReady(
        user(),
        { groupId: 'dailyFeatures', itemId: 'friendInvite' },
        now(),
      ),
    launch: async () => {
      await startInviteFriendsSequence(
        Feature.INVITE._,
        Feature.INVITE.INVITE_LAUNCH,
      );
      // Show-resolved: closing or cancelling starts the item cooldown.
      return true;
    },
  },
};

/**
 * Launch surfaces for recurring features.
 *
 * Recurring features can consume one remaining shared launch slot. Their
 * priority remains defined by `promoGroupConfig` in the Replicant ruleset.
 */
const recurringFeatures: PromoLaunchItems['recurringFeatures'] = {
  /**
   * Magic Lamps (Action dialog, recurring per event).
   *
   * Entry point to the lamp-rubbing minigame. User must choose to play.
   * High volume (4M/mo), limited discovery besides HUD icon.
   * Time-sensitive: Mon-Tue.
   *
   * Available when:
   * - Magic lamps event is ongoing and feature is unlocked
   *
   * Popup: `PopupMagicLamps` — shows lamp info and play option.
   */
  magicLamps: {
    isAvailable: () => {
      return magicLampsIsAvailable(user(), now());
    },
    // Uncollected lamp rewards open the popup on every launch and never take a slot.
    shouldExempt: () => magicLampsGetRewardSet(user()).length > 0,
    launch: async () => {
      await magicLampsLaunch();
      return true;
    },
  },

  /**
   * Streak Quest intro (Action dialog, recurring per daily reset).
   *
   * Asks user to opt into streak mode. Lowest priority because
   * post-play SQ progress popup fires every game as fallback.
   *
   * Available when:
   * - Streak event enabled
   * - Not already active (not restarting from expired state)
   * - Intro not seen since the last feature reset (`streak.introSeen`)
   *
   * Popup: `PopupStreakBattleRoyaleV2` — streak intro with opt-in.
   */
  streakQuest: {
    // Mirrors the Control launch gate. Only a genuine feature reset clears introSeen.
    // A rewarded event opens no popup, so it must not take a slot either.
    isAvailable: () =>
      isStreakBattleRoyaleEnabled(user()) &&
      !isStreakBattleRoyaleActive(user(), now()) &&
      !isStreakBattleRoyaleRewarded(user()) &&
      !user().streak?.introSeen,
    // The popup returns false when nothing was shown (e.g. rewarded), so promoGroups
    // state stays untouched.
    launch: () => openStreakBattleRoyalePopup('launch'),
  },

  /**
   * Invite Challenge (Action dialog, recurring daily).
   *
   * Encourages users to invite LINE friends during the active wallpaper
   * challenge. The promo group cooldown limits the popup to once per JST day.
   *
   * Available when:
   * - Friend referrals are enabled
   * - The 24-hour invite challenge is running and unclaimed
   *
   * Popup: `PopupInviteChallenge` — opens the friend invite flow from its CTA.
   */
  inviteChallenge: {
    isAvailable: () => {
      return Social.getters.isInviteChallengeRunning(user(), now());
    },
    launch: async () => {
      await openPopupPromise('popupInviteChallenge', {});
      return true;
    },
  },

  /**
   * Janken event promo (Action dialog, recurring per event).
   *
   * Launches the janken sender sequence: shows tutorial on first
   * open, then opens the challenge popup for the user to play.
   *
   * Available when:
   * - Janken event is active (schedule)
   * - User has unlocked janken (level gated)
   * - Pet is not blocked (dead, asleep, wantToGoOut, daycare)
   *
   * Popup: `PopupJankenInfo` (FTUE tutorial) then `PopupJankenChallenge`.
   */
  jankenEventPromo: {
    isAvailable: () => {
      // Skip if janken was already shown via invite/result entry earlier
      // in the launch sequence (jankenLaunchSequence handles those).
      return (
        !isJankenInviteEntry() &&
        !isJankenResultEntry() &&
        jankenIsEventActive(user(), now()) &&
        jankenIsAvailable(user()) &&
        getJankenBlockedReason() === null
      );
    },
    launch: async () => {
      await jankenSenderSequence('launch');
      return true;
    },
  },

  /**
   * Decoration Gacha promo (Info dialog, recurring per event).
   *
   * Introduces the current Gacha event using its configured promo and existing
   * promo cooldown.
   *
   * Available when:
   * - The current Gacha event has a promo configured
   * - The Gacha event is available to the player
   * - The existing promo cooldown is ready
   *
   * Popup: `PopupGachaIntro`.
   */
  gacha: {
    isAvailable: () => {
      return shouldShowGachaPromo();
    },
    launch: async () => {
      await showGachaPromo();
      return true;
    },
  },

  /**
   * Treasure Express (Action dialog, recurring per event).
   *
   * Reuses the Treasure Express launch sequence. Unseen ongoing events compete
   * for a recurring slot. Unclaimed rewards are exempt at launch only.
   *
   * Available when:
   * - Ongoing treasure express event exists
   * - User has unclaimed milestones or has not initialized this event
   *
   * Popup: `PopupTreasureExpress` — info (FTUE) or leaderboard.
   */
  treasureExpress: {
    isAvailable: () => {
      const state = user();
      const timestamp = now();
      const event = getOngoingTreasureExpressEvent(state, timestamp);
      if (!event) return false;

      // Unclaimed milestones make the recurring surface available.
      if (getUnclaimedMilestones(state, timestamp)) return true;

      // First-ever Treasure Express discovery belongs to the FTUE slot.
      if (!state.treasureExpress?.lastSeenAt) return false;

      // Later unseen event instances use the recurring budget.
      return !state.treasureExpress.entries[event.id];
    },
    // Non-expired earned rewards bypass readiness, cooldown and budget at launch
    // (Control parity: treasureExpressLaunch shows them on every launch). Post-play ignores this.
    shouldExempt: () => Boolean(getUnclaimedMilestones(user(), now())),
    launch: async () => {
      await treasureExpressLaunch();
      return true;
    },
  },

  /**
   * Races intro (Action dialog, recurring per event).
   *
   * Primary join mechanism for races. User must choose to join.
   * Time-sensitive: Mon-Thu. Shows intro for new races, leaderboard for active.
   *
   * Available when:
   * - Races feature available (schedule active)
   *
   * Popup: `PopupRaceIntro` (new race) or `PopupRaceLeaderboard` (active race).
   * Reward-bearing race sequences can overflow the budget.
   */
  raceIntro: {
    isAvailable: () => {
      const state = user();
      const timestamp = now();

      // Pending race rewards always display their claim sequence at launch.
      if (getUnclaimedRaces(state, timestamp).length > 0) return true;

      // Current race content requires a race the player can participate in.
      if (!canParticipateOnCurrentRace(state, timestamp)) return false;

      // Participation guarantees an active race, but handle missing schedule data.
      const activeRace = getActiveRace(timestamp);
      if (!activeRace) return false;

      // Do not spend a promo slot after this race was automatically shown.
      const activeRaceIndex = getActiveRaceIndex(state, timestamp);
      const { lastViewedRaceScore } = state.match2;
      const wasCurrentRaceAutoShown =
        lastViewedRaceScore?.raceId === activeRace.id &&
        lastViewedRaceScore.raceIndex === activeRaceIndex &&
        lastViewedRaceScore.autoShowed === true;

      // Only an unseen current race has content left to display automatically.
      return !wasCurrentRaceAutoShown;
    },
    shouldExempt: () => {
      return getUnclaimedRaces(user(), now()).length > 0;
    },
    launch: async () => {
      await openRaceSequence({ auto: true, originFeature: 'launch' });
      return true;
    },
  },

  /**
   * Champions League intro (Action dialog, recurring per league cycle).
   *
   * Introduces and joins the current Champions League after the player reaches
   * the end of the available Match2 levels.
   *
   * Available when:
   * - The player has completed the current maximum Match2 level
   * - The player has not joined the current league cycle
   *
   * Popup: `PopupChampionsLeagueInfo`, followed by the existing join flow.
   */
  championsLeague: {
    isAvailable: () => {
      return canPlayLeague(user()) && !hasJoinedLeague(user());
    },
    launch: async () => {
      await joinLeague('launch');
      return true;
    },
  },

  /**
   * Match2 Frenzy intro (Info dialog, recurring per event).
   *
   * Explains the active frenzy event after higher-priority recurring items.
   */
  frenzy: {
    isAvailable: () => {
      return (
        isMatch2FrenzyUnlocked(user()) &&
        isMatch2FrenzyEnabled(user(), now()) &&
        !getMatch2FrenzyIntroLastSeen(user()) &&
        getCurrentPetState(user(), now()) !== 'dead'
      );
    },
    launch: async () => {
      await openMatch2FrenzyIntroPopup({
        skipPlay: true,
        setLastOpened: true,
        origin: 'launch',
      });
      return true;
    },
  },
};

/**
 * Launch surfaces for first-time user experience features.
 *
 * FTUE features use an independent reserved slot. Lifetime intros show once,
 * while event intros use their configured recurrence. Their priority remains
 * defined by `promoGroupConfig` in the Replicant ruleset.
 */
const ftueFeatures: PromoLaunchItems['ftueFeatures'] = {
  /**
   * Baking Buddies (Action dialog, showOnce).
   *
   * Uses the dedicated FTUE slot once per lifetime, for players who never saw
   * any Buddies event. Party-join rewards are exempt and run the Control
   * launch flow without touching the FTUE state.
   *
   * Available when:
   * - Buddies feature available or locked (invited but not unlocked)
   * - Unseen party joiners exist, or no event was ever marked seen
   *
   * Popup: Existing Buddies main-popup sequence, or the join-reward launch
   * sequence when exempt.
   */
  buddies: {
    isAvailable: () => {
      const state = user();
      const timestamp = now();
      const isAccessible =
        buddiesIsAvailable(state, timestamp) ||
        buddiesIsLocked(state, timestamp);
      if (!isAccessible) return false;
      // Earned party-join rewards always have a launch path (exempt below).
      if (buddiesGetUnseenPartyJoinerIds().length > 0) return true;
      // First-ever discovery only, across reloads and new events.
      return !buddiesHasSeenAnyEvent(state);
    },
    // Same activity check Control's launch block uses. Never counts against a slot.
    shouldExempt: () => buddiesGetUnseenPartyJoinerIds().length > 0,
    launch: async () => {
      await buddiesOpenMainPopup('launch');
      return true;
    },
    launchExempt: async () => {
      // Join rewards + main popup on activity — the Control launch flow.
      await buddiesLaunchSequence();
      // Not a discovery: leave the FTUE showOnce untouched.
      return false;
    },
  },

  /**
   * Dream Drift event introduction (Action dialog, recurring per event).
   *
   * Uses the current event's seen state while the shared sequence optionally
   * presents the lifetime FTUE before the signup popup and progression map.
   */
  dreamDrift: {
    isAvailable: () => {
      // Leave the event introduction eligible for a later launch after waking.
      if (getCurrentPetState(user(), now()) === 'asleep') return false;

      // Unclaimed run rewards stay available even outside the event window.
      if (dreamDriftHasRewardsAvailable(user(), now())) return true;

      // Preserve the existing event and promo eligibility checks.
      return dreamDriftShouldAutoOpenCurrentEvent(user(), now());
    },
    // Unclaimed run rewards open the map on every launch and never take a slot.
    shouldExempt: () => dreamDriftHasRewardsAvailable(user(), now()),
    launch: async () => {
      await dreamDriftOpen();
      return true;
    },
  },

  /**
   * Super Lollipop info (Info dialog, showOnce).
   *
   * Introduces the super lollipop win-streak mechanic (L100+ users).
   * Fallback discovery: post-game SL activation popup.
   *
   * Available when:
   * - Feature available (level 100+, schedule active)
   * - User has never seen the feature (`!getSuperLollipopLastSeen`)
   *
   * Popup: Shows info popup (FTUE), then progress or active popup.
   */
  superLollipop: {
    isAvailable: () => {
      return (
        isSuperLollipopAvailable(user()) && !getSuperLollipopLastSeen(user())
      );
    },
    launch: async () => {
      await superLollipopLaunch();
      return true;
    },
  },

  /**
   * Home shortcut promo (Info dialog, showOnce).
   *
   * Reuses the existing Home Shortcut sequence. HOMESCREEN rewards can overflow
   * the budget; only players with no legacy popup history use the FTUE surface.
   *
   * Available when either a HOMESCREEN reward is ready, or:
   * - Feature flag enabled
   * - Platform supports shortcut creation (`canCreateShortcut`)
   * - Native app promo is not showing (avoids conflict)
   * - The legacy launch popup has never been shown and its cooldown is ready
   *
   * Popup: `PopupHomeShortcutReward` when exempt, or `PopupHomeShortcut` as FTUE.
   */
  homeShortcut: {
    isAvailable: () => {
      const state = user();
      const timestamp = now();
      const hasLaunchReward =
        platform.entryData?.$channel === 'HOMESCREEN' &&
        isPromoReady(state, 'homeShortcutLaunch', timestamp);

      // A ready launch reward makes the existing sequence available.
      if (hasLaunchReward) return true;

      // Match the legacy popup's platform, conflict, history, and cooldown rules.
      return (
        platform.canCreateShortcut &&
        !isNativePromoAvailable() &&
        getPromoShownCounter(state, 'homeShortcutPopupAtLaunch') === 0 &&
        isPromoReady(state, 'homeShortcutPopupAtLaunch', timestamp)
      );
    },
    shouldExempt: () => {
      // Only an explicit Home Shortcut entry can grant the launch reward.
      if (platform.entryData?.$channel !== 'HOMESCREEN') return false;

      // The existing promo state remains the source of reward readiness.
      return isPromoReady(user(), 'homeShortcutLaunch', now());
    },
    launch: async () => {
      await homeShortcutLaunchSequence();
      return true;
    },
  },

  /**
   * Cards collection discovery (Info dialog, showOnce).
   *
   * Uses the existing lifetime Cards first-view state and discovery sequence.
   */
  cards: {
    isAvailable: () => {
      return cardsShouldAutoOpenFirstTime(user());
    },
    launch: () => {
      return cardsAutoOpenFirstTime('launch');
    },
  },

  /**
   * Janken discovery (Action dialog, showOnce).
   *
   * Uses the lifetime tutorial state while retaining later event promos in the
   * recurring group.
   */
  janken: {
    isAvailable: () => {
      // Mirror the recurring launch constraints for first-ever discovery.
      return (
        !jankenHasSeenTutorial(user()) &&
        !isJankenInviteEntry() &&
        !isJankenResultEntry() &&
        jankenIsEventActive(user(), now()) &&
        jankenIsAvailable(user()) &&
        getJankenBlockedReason() === null
      );
    },
    launch: async () => {
      await jankenSenderSequence('launch');
      return true;
    },
  },

  /**
   * Treasure Express discovery (Action dialog, showOnce).
   *
   * Uses lifetime feature history while retaining later event promos and
   * reward claims in the recurring group.
   */
  treasureExpress: {
    isAvailable: () => {
      const state = user();
      const timestamp = now();

      // Reward-bearing launches remain exempt recurring content.
      if (getUnclaimedMilestones(state, timestamp)) return false;

      // Treasure Express must have an accessible ongoing event.
      if (!getOngoingTreasureExpressEvent(state, timestamp)) return false;

      // Only players with no lifetime view qualify for the FTUE slot.
      return !state.treasureExpress?.lastSeenAt;
    },
    launch: async () => {
      await treasureExpressLaunch();
      return true;
    },
  },
};

/**
 * Launch surfaces for the promo group model.
 *
 * Group membership and priority remain defined by `promoGroupConfig`. These
 * maps only connect each typed item ID to its existing frontend availability
 * check and launcher.
 */
export const promoLaunchItems: PromoLaunchItems = {
  dailyFeatures,
  recurringFeatures,
  ftueFeatures,
};

/**
 * Resolves a promo-group feature after another surface displays it.
 *
 * @param item Promo-group feature that was displayed.
 * @returns A promise that completes after treatment state is updated.
 */
export async function resolvePromoGroupFeature(
  item: PromoGroupItem,
): Promise<void> {
  // Read the latest state before treatment and readiness checks.
  const state = user();

  // Control users do not use the centralized promo-group ledger.
  if (!features(state).suppressFeatureIntros) return;

  // Already-resolved items do not need another Replicant action call.
  if (!isGroupItemReady(state, item, now())) return;

  // Record the display before a later promo-group trigger evaluates readiness.
  await StateObserver.invoke.groupItemWasResolved(item);
}

/**
 * Runs the treatment launch promo groups with their shared and extra slots.
 *
 * @returns False so the surrounding launch sequence continues.
 */
export async function launchPromoGroups(): Promise<boolean> {
  // Read group IDs once so both slot pools preserve their configured order.
  const groupIds = Object.keys(promoGroupConfig) as PromoGroupID[];

  // Shared groups consume the standard launch slots in configured order.
  let remainingSlots = launchPromoSlotsAvailable;
  for (const groupId of groupIds) {
    const groupConfig = promoGroupConfig[groupId];
    if (groupConfig.maxSlots === undefined) continue;

    const groupSlots = Math.min(groupConfig.maxSlots, remainingSlots);
    const slotsUsed = await processGroup(groupId, groupSlots);
    remainingSlots -= slotsUsed;
  }

  // Extra-slot groups process their independent budgets after shared groups.
  for (const groupId of groupIds) {
    const groupConfig = promoGroupConfig[groupId];
    if (groupConfig.extraSlots === undefined) continue;

    await processGroup(groupId, groupConfig.extraSlots);
  }

  // Promo groups no longer stop the surrounding launch sequence.
  return false;
}

/**
 * Runs one recurring promo after a completed Match2 post-play flow.
 *
 * @returns Whether an eligible recurring promo item was displayed.
 */
export async function postPlayPromoGroups(): Promise<boolean> {
  // Tutorial puzzles must not trigger popups before the final toy interaction.
  if (puzzleCoinsIsPlayingTutorial(user())) return false;

  // Post-play uses one independent recurring-feature slot with no overflow.
  const slotsUsed = await processGroup('recurringFeatures', 1, true);

  // Report whether the session consumed its post-play promo opportunity.
  return slotsUsed > 0;
}

/**
 * Processes every item in one launch promo group.
 *
 * @param groupId Group whose ready launch items should be processed.
 * @param maxSlots Maximum slots available to the group for this launch.
 * @param stopWhenSlotsFilled Whether to stop after the supplied slot budget is
 * filled. Also disables exemptions, so only ready items with a slot display.
 * @returns Number of slots consumed by displayed items.
 */
async function processGroup<GroupID extends PromoGroupID>(
  groupId: GroupID,
  maxSlots: number,
  stopWhenSlotsFilled = false,
): Promise<number> {
  // Retrieve the group's item ordering and recurrence configuration.
  const groupConfig = promoGroupConfig[groupId];

  // Read normal recurrence only while this group can still consume a slot.
  let readyItems: PromoGroupItemID<GroupID>[] = [];
  if (maxSlots > 0) {
    readyItems = getReadyGroupItems(user(), groupId, now());
  }

  // Build the lookup used by normal item processing and dynamic priority.
  const readyItemSet = new Set(readyItems);
  let items: PromoGroupItemID<GroupID>[] = [];

  // Fixed-priority groups process every item in ruleset order.
  if ((groupConfig.priorityMode ?? 'neverSeenFirst') === 'configured') {
    items = groupConfig.items.map((item) => {
      return item.id;
    });
  } else {
    // Dynamic groups preserve ready priority before checking non-ready exemptions.
    items.push(...readyItems);
    for (const item of groupConfig.items) {
      if (readyItemSet.has(item.id)) continue;
      items.push(item.id);
    }
  }

  // Process every item so exemptions can overflow a filled group budget.
  const groupSurfaces: Record<string, PromoItemSurface> =
    promoLaunchItems[groupId];
  let slotsUsed = 0;
  for (const itemId of items) {
    const usedSlot = await processItem({
      groupId,
      itemId,
      surface: groupSurfaces[itemId],
      isReady: readyItemSet.has(itemId),
      hasSlot: slotsUsed < maxSlots,
      // Post-play never overflows with exempt content: earned rewards already have
      // post-game surfaces (treasureExpressPostGame, openRaceSequence). Keeps #11711 behaviour.
      allowExempt: !stopWhenSlotsFilled,
    });

    if (usedSlot) {
      slotsUsed++;
    }

    // Targeted triggers must never display more items than their strict cap.
    if (stopWhenSlotsFilled && slotsUsed >= maxSlots) break;
  }

  // Return only slots consumed from this group's supplied budget.
  return slotsUsed;
}

/**
 * Processes one launch promo item.
 *
 * @param item Surface and launch state for the item being considered. Exempt
 * content is only considered while `allowExempt` is set.
 * @returns Whether the displayed item consumed a slot from the group budget.
 */
async function processItem(item: {
  groupId: PromoGroupID;
  itemId: PromoGroupItemID;
  surface: PromoItemSurface;
  isReady: boolean;
  hasSlot: boolean;
  allowExempt: boolean;
}): Promise<boolean> {
  // Exemptions need both the trigger's permission and a surface hook.
  const canExempt = item.allowExempt && item.surface.shouldExempt !== undefined;

  // Items that cannot be exempt need both readiness and an available slot.
  if (!canExempt && (!item.isReady || !item.hasSlot)) return false;

  // Unavailable features cannot display or qualify for exemption.
  if (!item.surface.isAvailable()) return false;

  // Available exempt content can overflow a filled budget.
  const isExempt = canExempt && item.surface.shouldExempt?.() === true;

  // Non-exempt content requires normal recurrence readiness.
  if (!isExempt && !item.isReady) return false;

  // Non-exempt content cannot display after the group fills its slots.
  if (!isExempt && !item.hasSlot) return false;

  // Run alternate exempt content when the item provides it.
  let resolved: boolean;
  if (isExempt && item.surface.launchExempt) {
    resolved = await item.surface.launchExempt();
  } else {
    resolved = await item.surface.launch();
  }

  // Every promo group shares the same guarded resolution path. Unresolved
  // display attempts do not update the promo-group ledger.
  if (resolved) {
    await resolvePromoGroupFeature({
      groupId: item.groupId,
      itemId: item.itemId,
    });
  }

  // Exempt content never counts against a budget (spec class A). Non-exempt content
  // always had a slot here, resolved or not.
  return !isExempt;
}
